import { motion } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

const TIERS = [
  {
    name: "Wanderlust",
    price: 110,
    tagline: "Schengen Zone, Armenia, Singapore, Vietnam & Japan.",
    features: [
      "One visa application form, prepared",
      "Personalised document checklist",
      "One round of revisions",
      "48-hour turnaround",
    ],
    featured: false,
  },
  {
    name: "Venture",
    price: 300,
    tagline: "United States, United Kingdom",
    features: [
      "Full form set for your destination",
      "Cover letter & itinerary drafting",
      "Two-specialist accuracy review",
      "Priority 24-hour turnaround",
    ],
    featured: true,
  },
  {
    name: "Odyssey",
    price: 1200,
    tagline: "Canada Visa Full Application",
    features: [
      "Complete application, end to end",
      "Embassy interview preparation",
      "A dedicated named specialist",
      "Unlimited revisions until lodged",
    ],
    featured: false,
  },
];

export const Pricing = ({ onGetStarted }) => (
  <section
    data-testid="products-section"
    className="px-6 md:px-12 py-28 md:py-36"
  >
    <div className="max-w-[1400px] mx-auto">
      <div className="flex flex-wrap items-end justify-between gap-8 mb-20">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: EASE }}
            className="text-xs font-semibold tracking-[0.25em] uppercase text-slate-500 mb-5"
          >
            Packages
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
            className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-slate-900"
          >
            Pick your pace.
          </motion.h2>        </div>
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.2, ease: EASE }}
          className="max-w-sm text-sm text-slate-600 leading-relaxed"
        >
          One flat fee per file. No hourly meters, no surprise add-ons.
          Government and consulate fees are paid separately by you.
        </motion.p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 items-stretch">
        {TIERS.map((t, i) => (
          <motion.div
            key={t.name}
            data-testid={`pricing-card-${t.name.toLowerCase()}`}
            initial={{ opacity: 0, y: 48 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, delay: i * 0.12, ease: EASE }}
            className={`flex flex-col p-10 md:p-12 border ${
              t.featured
                ? "bg-slate-900 text-white border-slate-900 shadow-[0_40px_80px_-32px_rgba(15,23,42,0.45)]"
                : "bg-white text-slate-900 border-slate-200 hover:border-slate-400"
            } transition-colors duration-500`}
          >
            <div className="flex items-center justify-between">
              <p className={`font-display italic text-xl ${t.featured ? "text-slate-300" : "text-slate-500"}`}>
                {t.name}
              </p>
              {t.featured && (
                <span className="text-[10px] font-bold tracking-[0.25em] uppercase bg-white text-slate-900 px-3 py-1.5 rounded-full">
                  Most chosen
                </span>
              )}
            </div>

            <div className="mt-10 flex items-baseline gap-2">
              <span className="font-display text-6xl tracking-tight" data-testid={`price-${t.name.toLowerCase()}`}>
                ${t.price}
              </span>
              <span className={`text-sm ${t.featured ? "text-slate-400" : "text-slate-500"}`}>/ per file</span>
            </div>
            <p className={`mt-3 text-sm ${t.featured ? "text-slate-300" : "text-slate-600"}`}>{t.tagline}</p>

            <ul className={`mt-10 space-y-4 flex-1 border-t pt-10 ${t.featured ? "border-white/15" : "border-slate-200"}`}>
              {t.features.map((f) => (
                <li key={f} className="flex items-start gap-3 text-sm">
                  <Check className={`w-4 h-4 mt-0.5 shrink-0 ${t.featured ? "text-slate-300" : "text-slate-900"}`} />
                  <span className={t.featured ? "text-slate-200" : "text-slate-600"}>{f}</span>
                </li>
              ))}
            </ul>

            <button
              data-testid={`get-started-${t.name.toLowerCase()}`}
              onClick={() => onGetStarted(t.name)}
              className={`group mt-12 flex items-center justify-center gap-3 w-full py-4 text-sm font-semibold rounded-full transition-colors duration-300 ${
                t.featured
                  ? "bg-white text-slate-900 hover:bg-slate-200"
                  : "bg-slate-900 text-white hover:bg-slate-700"
              }`}
            >
              Get started
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);
